import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Chip, Button, Skeleton, Container,
  TextField, Drawer, IconButton, Divider, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  Select, MenuItem, Alert, useMediaQuery, useTheme,
} from '@mui/material';
import {
  FilterList as FilterIcon, Close as CloseIcon, Add as AddIcon, Article as ArticleIcon,
  ListAlt as ListAltIcon, Delete as DeleteIcon, Edit as EditIcon,
} from '@mui/icons-material';
import { blogService, type BlogArticle } from '../services/blogService';
import { locationsAPI, type EventType, type City, organizersAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [canWrite, setCanWrite] = useState(false);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);

  // Subscriptions state
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [subForm, setSubForm] = useState({ event_type_id: null as number | null, author_id: null as number | null, city_id: null as number | null, delivery_method: 'notification' });
  const [subAuthorSearch, setSubAuthorSearch] = useState<any[]>([]);
  const [subSelectedAuthor, setSubSelectedAuthor] = useState<any>(null);
  const [editingSubId, setEditingSubId] = useState<number | null>(null);
  const [subError, setSubError] = useState('');

  const authorId = searchParams.get('author_id') ? Number(searchParams.get('author_id')) : undefined;
  const [filters, setFilters] = useState<any>({});
  const [authorSearchResults, setAuthorSearchResults] = useState<any[]>([]);
  const [selectedAuthor, setSelectedAuthor] = useState<any>(null);

  useEffect(() => {
    locationsAPI.getEventTypes().then(setEventTypes).catch(() => {});
    locationsAPI.getAllCities().then(setCities).catch(() => {});
    if (isAuthenticated) {
      blogService.listSubscriptions().then(setSubscriptions).catch(() => {});
      blogService.checkAccess().then(a => setCanWrite(a.allowed)).catch(() => setCanWrite(false));
    }
  }, [isAuthenticated]);

  // Sync authorId from URL → selectedAuthor for filter drawer
  useEffect(() => {
    if (authorId && !selectedAuthor) {
      organizersAPI.search(String(authorId)).then(data => {
        const users = data.users || [];
        const match = users.find((u: any) => u.id === authorId);
        if (match) setSelectedAuthor(match);
      }).catch(() => {});
    }
    if (!authorId) {
      setSelectedAuthor(null);
    }
  }, [authorId]);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        setLoading(true);
        const params: any = { ...filters };
        // authorId from URL takes precedence if filters.author_id not set
        if (authorId && !params.author_id) params.author_id = authorId;
        const data = await blogService.listArticles(params);
        setArticles(data.articles);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    loadArticles();
  }, [filters, authorId]);

  const activeFiltersCount = Object.values(filters).filter(v => v !== undefined && v !== '').length + (authorId ? 1 : 0);

  const handleCreateSub = async () => {
    if (!subForm.event_type_id && !subForm.author_id && !subForm.city_id) {
      setSubError('Укажите хотя бы один фильтр');
      return;
    }
    try {
      const data = {
        event_type_id: subForm.event_type_id || undefined,
        author_id: subForm.author_id || undefined,
        city_id: subForm.city_id || undefined,
        delivery_method: subForm.delivery_method,
      };
      if (editingSubId) {
        await blogService.updateSubscription(editingSubId, data);
      } else {
        await blogService.createSubscription(data);
      }
      setSubDialogOpen(false);
      setSubForm({ event_type_id: null, author_id: null, city_id: null, delivery_method: 'notification' });
      setSubSelectedAuthor(null);
      setEditingSubId(null);
      setSubError('');
      blogService.listSubscriptions().then(setSubscriptions).catch(() => {});
    } catch (e: any) { setSubError(e.response?.data?.detail || 'Ошибка'); }
  };

  const handleDeleteSub = async (id: number) => {
    try { await blogService.deleteSubscription(id); setSubscriptions(subs => subs.filter(s => s.id !== id)); } catch {}
  };

  const formatDate = (s: string | null) => s ? new Date(s).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 0 } }}>
            <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
              {authorId ? 'Статьи автора' : 'Блог'}
            </Typography>
            {authorId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {articles[0]?.author_name && (
                  <Typography variant="body2" color="text.secondary">{articles[0].author_name}</Typography>
                )}
                <Chip label="Сбросить" size="small"
                  onDelete={() => { setSelectedAuthor(null); setFilters((f: any) => ({ ...f, author_id: undefined })); navigate('/blog'); }}
                  sx={{ height: 22, fontSize: '0.7rem' }} />
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {isAuthenticated && (
              <Button variant="outlined" startIcon={<ListAltIcon />} size="small"
                onClick={() => { setSubDialogOpen(true); setEditingSubId(null); setSubForm({ event_type_id: null, author_id: null, city_id: null, delivery_method: 'notification' }); setSubSelectedAuthor(null); }} sx={{ position: 'relative', flex: { xs: 1, sm: 'none' }, fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
                {isMobile ? `Подписки${subscriptions.length ? ` (${subscriptions.length})` : ''}` : 'Подписки'}
              </Button>
            )}
            <Button variant="outlined" startIcon={<FilterIcon />} size="small"
              onClick={() => setFilterOpen(true)} sx={{ position: 'relative', flex: { xs: 1, sm: 'none' }, fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
              {`Фильтры${activeFiltersCount ? ` (${activeFiltersCount})` : ''}`}
            </Button>
            {isAuthenticated && canWrite && (
              <Button variant="contained" startIcon={<AddIcon />} size="small"
                onClick={() => navigate('/blog/create')}
                sx={{ bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' }, flex: { xs: 1, sm: 'none' }, fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
                Написать
              </Button>
            )}
          </Box>
        </Box>

        {/* Article list */}
        {loading ? (
          <Grid container spacing={2}>{[1,2,3].map(i => <Grid item xs={12} sm={6} key={i}><Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} /></Grid>)}</Grid>
        ) : articles.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ArticleIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography color="text.secondary">Статей пока нет</Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {articles.map(article => (
              <Grid item xs={12} sm={6} md={4} key={article.id}>
                <Paper
                  component={RouterLink} to={`/blog/${article.id}`}
                  sx={{
                    textDecoration: 'none', color: 'inherit', cursor: 'pointer',
                    display: 'flex', flexDirection: article.cover_image ? 'row' : 'column',
                    height: '100%', overflow: 'hidden',
                    '&:hover': { borderColor: '#1565c0' },
                  }}
                >
                  {/* Text side */}
                  <Box sx={{
                    p: { xs: 2, sm: 2.5 },
                    display: 'flex', flexDirection: 'column',
                    flex: article.cover_image ? '0 0 60%' : 1,
                    minWidth: 0,
                  }}>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                      {article.event_type_name && (
                        <Chip size="small" label={`${article.event_type_emoji || ''} ${article.event_type_name}`} sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                      {article.city_name && (
                        <Chip size="small" label={article.city_name} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, mb: 0.5, lineHeight: 1.3 }}>
                      {article.title}
                    </Typography>
                    {article.excerpt && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 1, flex: 1 }}>
                        {article.excerpt}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" fontWeight={600} color="primary" onClick={e => { e.preventDefault(); e.stopPropagation(); navigate(`/blog?author_id=${article.author_id}`); }}>
                          {article.author_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">· {formatDate(article.published_at)}</Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Cover image side (only when cover_image exists) */}
                  {article.cover_image && (
                    <Box sx={{
                      flex: '0 0 40%',
                      backgroundImage: `url(${article.cover_image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      minHeight: { xs: 120, sm: 140 },
                    }} />
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Filter drawer */}
      <Drawer anchor="right" open={filterOpen} onClose={() => setFilterOpen(false)} sx={{ '& .MuiDrawer-paper': { width: 320, maxWidth: '90vw' } }}>
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>Фильтры</Typography>
            <IconButton onClick={() => setFilterOpen(false)} size="small"><CloseIcon /></IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="caption" fontWeight={700}>АВТОР СТАТЬИ</Typography>
            <Autocomplete
              fullWidth freeSolo
              options={authorSearchResults}
              filterOptions={o => o}
              getOptionLabel={option => {
                if (typeof option === 'string') return option;
                if ((option as any)._notFound) return '';
                return `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.username || '';
              }}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              renderInput={params => <TextField {...params} label="Автор" size="small" helperText="От 4 символов" />}
              onInputChange={async (_, value, reason) => {
                if (reason === 'reset' && !value) { setAuthorSearchResults([]); setFilters((f: any) => ({ ...f, author_id: undefined })); return; }
                if (value.length >= 4) {
                  try {
                    const data = await organizersAPI.search(value);
                    setAuthorSearchResults((data.users || []).length === 0 ? [{ id: -1, _notFound: true } as any] : data.users);
                  } catch { setAuthorSearchResults([{ id: -1, _notFound: true } as any]); }
                } else { setAuthorSearchResults([]); }
              }}
              onChange={(_, v) => {
                if (v && typeof v !== 'string' && !(v as any)._notFound) {
                  setSelectedAuthor(v);
                  setFilters((f: any) => ({ ...f, author_id: v.id }));
                  navigate(`/blog?author_id=${v.id}`, { replace: true });
                } else if (!v) {
                  setSelectedAuthor(null);
                  setFilters((f: any) => ({ ...f, author_id: undefined }));
                  navigate('/blog', { replace: true });
                }
              }}
              value={selectedAuthor}
            />
            <Typography variant="caption" fontWeight={700}>ПАРАМЕТРЫ</Typography>
            <Autocomplete options={eventTypes} getOptionLabel={o => `${o.icon_emoji || ''} ${o.name}`}
              renderInput={p => <TextField {...p} label="Вид мероприятия" size="small" />}
              value={eventTypes.find(t => t.id === filters.event_type_id) || null}
              onChange={(_, v) => setFilters((f: any) => ({ ...f, event_type_id: v?.id || undefined }))}
              isOptionEqualToValue={(o, v) => o.id === v?.id} size="small" />
            <Autocomplete options={cities} getOptionLabel={o => o.name}
              renderInput={p => <TextField {...p} label="Город" size="small" />}
              value={cities.find(c => c.id === filters.city_id) || null}
              onChange={(_, v) => setFilters((f: any) => ({ ...f, city_id: v?.id || undefined }))}
              isOptionEqualToValue={(o, v) => o.id === v?.id} size="small" />
            <TextField label="Дата от" type="date" size="small" InputLabelProps={{ shrink: true }}
              value={filters.date_from || ''} onChange={e => setFilters((f: any) => ({ ...f, date_from: e.target.value || undefined }))} />
            <TextField label="Дата до" type="date" size="small" InputLabelProps={{ shrink: true }}
              value={filters.date_to || ''} onChange={e => setFilters((f: any) => ({ ...f, date_to: e.target.value || undefined }))} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="outlined" fullWidth size="small" onClick={() => { setFilters({}); setSelectedAuthor(null); navigate('/blog'); }}>Сбросить</Button>
            <Button variant="contained" fullWidth size="small" onClick={() => setFilterOpen(false)}>Применить</Button>
          </Box>
        </Box>
      </Drawer>

      {/* Subscription dialog */}
      <Dialog open={subDialogOpen} onClose={() => setSubDialogOpen(false)} fullScreen={isMobile} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Мои подписки
          <IconButton onClick={() => setSubDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Current subscriptions */}
          {subscriptions.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              {subscriptions.map(sub => (
                <Paper key={sub.id} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                        {sub.event_type_name && <Chip size="small" label={`${sub.event_type_emoji || ''} ${sub.event_type_name}`} sx={{ height: 22, fontSize: '0.7rem' }} />}
                        {sub.author_name && <Chip size="small" label={`Автор: ${sub.author_name}`} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
                        {sub.city_name && <Chip size="small" label={`📍 ${sub.city_name}`} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
                      </Box>
                      <Chip size="small"
                        label={sub.delivery_method === 'both' ? '🔔 Колокольчик + Почта' : sub.delivery_method === 'email' ? '📧 Почта' : '🔔 Колокольчик'}
                        color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <IconButton size="small" onClick={() => {
                        setSubForm({ event_type_id: sub.event_type_id, author_id: sub.author_id, city_id: sub.city_id, delivery_method: sub.delivery_method });
                        setEditingSubId(sub.id);
                        // Find and set the author in search results if exists
                        if (sub.author_id && sub.author_name) {
                          setSubSelectedAuthor({ id: sub.author_id, first_name: sub.author_name, last_name: '', username: '' });
                        }
                      }} title="Изменить"><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteSub(sub.id)} title="Удалить"><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              У вас пока нет подписок
            </Typography>
          )}

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>{editingSubId ? 'Редактирование подписки' : 'Новая подписка'}</Typography>

          {subError && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }} onClose={() => setSubError('')}>{subError}</Alert>}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete options={eventTypes} getOptionLabel={o => `${o.icon_emoji || ''} ${o.name}`}
              renderInput={p => <TextField {...p} label="Вид мероприятия" size="small" helperText="Оставьте пустым для всех видов" />}
              value={eventTypes.find(t => t.id === subForm.event_type_id) || null}
              onChange={(_, v) => setSubForm({ ...subForm, event_type_id: v?.id || null })}
              isOptionEqualToValue={(o, v) => o.id === v?.id} size="small" />

            <Typography variant="caption" fontWeight={700}>АВТОР</Typography>
            <Autocomplete
              fullWidth freeSolo
              options={subAuthorSearch}
              filterOptions={o => o}
              getOptionLabel={option => {
                if (typeof option === 'string') return option;
                if ((option as any)._notFound) return '';
                return `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.username || '';
              }}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              renderInput={params => <TextField {...params} label="Автор" size="small" helperText="От 4 символов, оставьте пустым для всех" />}
              onInputChange={async (_, value, reason) => {
                if (reason === 'reset' && !value) { setSubAuthorSearch([]); setSubForm({ ...subForm, author_id: null }); return; }
                if (value.length >= 4) {
                  try {
                    const data = await organizersAPI.search(value);
                    setSubAuthorSearch((data.users || []).length === 0 ? [{ id: -1, _notFound: true } as any] : data.users);
                  } catch { setSubAuthorSearch([{ id: -1, _notFound: true } as any]); }
                } else { setSubAuthorSearch([]); }
              }}
              onChange={(_, v) => {
                if (v && typeof v !== 'string' && !(v as any)._notFound) { setSubSelectedAuthor(v); setSubForm({ ...subForm, author_id: v.id }); }
                else if (!v) { setSubSelectedAuthor(null); setSubForm({ ...subForm, author_id: null }); }
              }}
              value={subSelectedAuthor}
            />

            <Autocomplete options={cities} getOptionLabel={o => o.name}
              renderInput={p => <TextField {...p} label="Город" size="small" helperText="Оставьте пустым для всех городов" />}
              value={cities.find(c => c.id === subForm.city_id) || null}
              onChange={(_, v) => setSubForm({ ...subForm, city_id: v?.id || null })}
              isOptionEqualToValue={(o, v) => o.id === v?.id} size="small" />

            <FormControl size="small">
              <InputLabel>Способ уведомления</InputLabel>
              <Select value={subForm.delivery_method} label="Способ уведомления"
                onChange={e => setSubForm({ ...subForm, delivery_method: e.target.value })}>
                <MenuItem value="notification">🔔 Под колокольчиком</MenuItem>
                <MenuItem value="email">📧 На почту</MenuItem>
                <MenuItem value="both">🔔📧 Колокольчик + почта</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubDialogOpen(false)}>Закрыть</Button>
          {editingSubId && <Button onClick={() => { setEditingSubId(null); setSubForm({ event_type_id: null, author_id: null, city_id: null, delivery_method: 'notification' }); setSubSelectedAuthor(null); }}>Отмена</Button>}
          <Button onClick={handleCreateSub} variant="contained" sx={{ bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' } }}>{editingSubId ? 'Сохранить' : 'Подписаться'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BlogPage;